import { useEffect, useMemo, useState } from "react";
import { Search, Download, X, Save, Phone, Mail, Building2, Briefcase, Tag, Loader2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "./api";
import { ScoreBadge } from "./AdminDashboard";
import { usePageMeta } from "../hooks/usePageMeta";
import { waLink } from "../brand";

const SERVICES = [
  "", "Automatización IA", "Implementación Respond.io", "Google Ads",
  "Meta Ads", "TikTok Ads", "Funnels de Venta", "Consultoría Estratégica",
];

const STATUSES = [
  "Nuevo", "Contactado", "Reunión Agendada", "Propuesta Enviada",
  "Negociación", "Cerrado Ganado", "Cerrado Perdido",
];
const PRIORITIES = ["Baja", "Media", "Alta", "Urgente"];

const fmtDate = (iso) => {
  try {
    const d = typeof iso === "string" ? new Date(iso) : new Date(iso);
    return d.toLocaleString("es-GT", { dateStyle: "medium", timeStyle: "short" });
  } catch { return ""; }
};

const statusColor = (s) => ({
  "Nuevo": "bg-[#ff8a00]/15 text-[#ffd23f] border-[#ff8a00]/30",
  "Contactado": "bg-blue-500/10 text-blue-300 border-blue-400/20",
  "Reunión Agendada": "bg-cyan-500/10 text-cyan-300 border-cyan-400/20",
  "Propuesta Enviada": "bg-amber-500/10 text-amber-300 border-amber-400/20",
  "Negociación": "bg-orange-500/10 text-orange-300 border-orange-400/20",
  "Cerrado Ganado": "bg-emerald-500/10 text-emerald-300 border-emerald-400/30",
  "Cerrado Perdido": "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
}[s] || "bg-white/[0.04] text-zinc-300 border-white/[0.08]");

export default function AdminLeads() {
  usePageMeta({ title: "Leads · Daos CRM" });
  const [filters, setFilters] = useState({
    q: "", service_interest: "", lead_status: "", priority: "", lead_source: "",
  });
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await adminApi.get("/admin/leads", { params: { ...params, limit: 200 } });
      setLeads(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("No se pudieron cargar los leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); /* eslint-disable-next-line */ }, []);

  const onFilter = (e) => setFilters((f) => ({ ...f, [e.target.name]: e.target.value }));
  const apply = (e) => { e.preventDefault(); fetchLeads(); };
  const reset = () => { setFilters({ q: "", service_interest: "", lead_status: "", priority: "", lead_source: "" }); setTimeout(fetchLeads, 0); };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await adminApi.get("/admin/leads/export.csv", {
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `daos-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch {
      toast.error("No se pudo exportar el CSV");
    } finally {
      setExporting(false);
    }
  };

  const sources = useMemo(() => {
    const canonical = [
      "Google Ads",
      "Facebook Ads",
      "Instagram Ads",
      "Meta Ads",
      "TikTok Ads",
      "LinkedIn Ads",
      "Orgánico",
      "Orgánico · Instagram",
      "Orgánico · Facebook",
      "Orgánico · TikTok",
      "Referido",
      "Referido · Social",
      "Directo",
    ];
    const fromLeads = leads.map((l) => l.lead_source).filter(Boolean);
    const merged = Array.from(new Set([...canonical, ...fromLeads]));
    return ["", ...merged];
  }, [leads]);

  const onSaved = (updated) => {
    setLeads((arr) => arr.map((l) => (l.id === updated.id ? updated : l)));
    setEditing(null);
    toast.success("Lead actualizado");
  };

  return (
    <div className="container-x py-8 lg:py-12">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <div className="eyebrow mb-3">CRM · Pipeline</div>
          <h1 className="font-display font-extrabold text-white text-3xl sm:text-4xl tracking-tighter">
            Leads {total > 0 && <span className="text-zinc-500 text-2xl font-normal">· {total}</span>}
          </h1>
          <p className="mt-2 text-zinc-400 text-sm">Buscar, filtrar, calificar y exportar leads del sitio.</p>
        </div>
        <button
          onClick={exportCSV}
          disabled={exporting}
          className="btn-primary !py-2.5 !px-5 text-sm disabled:opacity-60"
          data-testid="leads-export-csv"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {exporting ? "Exportando..." : "Exportar CSV"}
        </button>
      </div>

      {/* Filters */}
      <form onSubmit={apply} className="grad-border p-4 sm:p-5 grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="leads-filters">
        <div className="col-span-2 lg:col-span-2 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            name="q"
            value={filters.q}
            onChange={onFilter}
            placeholder="Buscar nombre, empresa, email..."
            className="w-full bg-black/40 border border-white/[0.08] rounded-xl pl-10 pr-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:border-[#ff8a00] focus:ring-2 focus:ring-[#ff8a00]/20 transition outline-none"
            data-testid="leads-filter-q"
          />
        </div>
        <FilterSelect name="service_interest" value={filters.service_interest} onChange={onFilter} options={SERVICES} placeholder="Todos los servicios" />
        <FilterSelect name="lead_status" value={filters.lead_status} onChange={onFilter} options={["", ...STATUSES]} placeholder="Todos los estados" />
        <FilterSelect name="priority" value={filters.priority} onChange={onFilter} options={["", ...PRIORITIES]} placeholder="Toda prioridad" />
        <FilterSelect name="lead_source" value={filters.lead_source} onChange={onFilter} options={sources} placeholder="Toda fuente" />
        <div className="col-span-2 lg:col-span-6 flex items-center gap-2">
          <button type="submit" className="btn-primary !py-2.5 !px-5 text-sm" data-testid="leads-filter-apply">Aplicar</button>
          <button type="button" onClick={reset} className="btn-secondary !py-2.5 !px-5 text-sm" data-testid="leads-filter-reset">Limpiar</button>
        </div>
      </form>

      {/* Table */}
      <div className="mt-6 grad-border p-3 sm:p-5 overflow-x-auto">
        {loading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-10 rounded bg-white/[0.04] animate-pulse" />
            ))}
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-14 text-zinc-500">No hay leads que coincidan con los filtros.</div>
        ) : (
          <table className="w-full text-sm min-w-[760px]" data-testid="leads-table">
            <thead>
              <tr className="text-[10px] tracking-[0.18em] uppercase text-zinc-500">
                <th className="text-left font-display font-semibold py-2 px-2">Nombre</th>
                <th className="text-left font-display font-semibold py-2 px-2">Empresa</th>
                <th className="text-left font-display font-semibold py-2 px-2">Servicio</th>
                <th className="text-left font-display font-semibold py-2 px-2">Presupuesto</th>
                <th className="text-left font-display font-semibold py-2 px-2">Estado</th>
                <th className="text-left font-display font-semibold py-2 px-2">Score</th>
                <th className="text-left font-display font-semibold py-2 px-2">Fecha</th>
                <th className="text-right font-display font-semibold py-2 px-2"> </th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-white/[0.05] hover:bg-white/[0.02] transition cursor-pointer" onClick={() => setEditing(l)} data-testid={`lead-row-${l.id}`}>
                  <td className="py-3 px-2">
                    <div className="text-white font-display font-semibold">{l.full_name || "—"}</div>
                    <div className="text-zinc-500 text-xs">{l.email}</div>
                  </td>
                  <td className="py-3 px-2 text-zinc-300">
                    <div>{l.company_name || "—"}</div>
                    <div className="text-zinc-500 text-xs">{l.job_title || ""}</div>
                  </td>
                  <td className="py-3 px-2 text-zinc-300">{l.service_interest || "—"}</td>
                  <td className="py-3 px-2 text-zinc-300">{l.budget_range || "—"}</td>
                  <td className="py-3 px-2">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-display border ${statusColor(l.lead_status)}`}>
                      {l.lead_status || "Nuevo"}
                    </span>
                  </td>
                  <td className="py-3 px-2"><ScoreBadge value={l.lead_score || 0} /></td>
                  <td className="py-3 px-2 text-zinc-500 text-xs">{fmtDate(l.created_at)}</td>
                  <td className="py-3 px-2 text-right">
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#ff8a00] font-display">Editar →</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && <LeadDrawer lead={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
    </div>
  );
}

function FilterSelect({ name, value, onChange, options, placeholder }) {
  return (
    <select
      name={name}
      value={value}
      onChange={onChange}
      data-testid={`leads-filter-${name}`}
      className="bg-black/40 border border-white/[0.08] rounded-xl px-3 py-2.5 text-white text-sm focus:border-[#ff8a00] focus:ring-2 focus:ring-[#ff8a00]/20 transition outline-none appearance-none cursor-pointer"
    >
      {options.map((o, i) => (
        <option key={`${name}-${i}-${o}`} value={o} className="bg-[#0a0a0a] text-white">
          {o || placeholder}
        </option>
      ))}
    </select>
  );
}

function LeadDrawer({ lead, onClose, onSaved }) {
  const [form, setForm] = useState({
    lead_status: lead.lead_status || "Nuevo",
    priority: lead.priority || "Media",
    internal_notes: lead.internal_notes || "",
    assigned_to: lead.assigned_to || "David Osorio",
    lead_score: lead.lead_score ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.patch(`/admin/leads/${lead.id}`, {
        ...form,
        lead_score: Number(form.lead_score),
      });
      onSaved(data);
    } catch (err) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" data-testid="lead-drawer">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative ml-auto w-full sm:max-w-lg h-full bg-[#070707] border-l border-white/[0.08] overflow-y-auto">
        <div className="sticky top-0 bg-[#070707]/90 backdrop-blur-xl border-b border-white/[0.06] flex items-center justify-between px-5 py-4 z-10">
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#ff8a00] font-display">Lead detalle</div>
            <div className="text-white font-display font-bold">{lead.full_name}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass grid place-items-center hover:border-[#ff8a00]/40" data-testid="lead-drawer-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info icon={Building2} label="Empresa" value={lead.company_name} />
            <Info icon={Briefcase} label="Cargo" value={lead.job_title || "—"} />
            <Info icon={Mail} label="Correo" value={lead.email} link={`mailto:${lead.email}`} />
            <Info icon={Phone} label="WhatsApp" value={lead.phone} link={waLink("Hola, soy David. " + (lead.full_name || "") + ", ¿cómo te ayudo?")} target />
            <Info icon={Tag} label="Servicio" value={lead.service_interest || "—"} />
            <Info icon={Tag} label="Presupuesto" value={lead.budget_range || "—"} />
          </div>

          <div className="grad-border p-4">
            <div className="text-[10px] tracking-[0.18em] uppercase text-[#ff8a00] font-display font-semibold mb-2">Necesidad</div>
            <p className="text-zinc-200 text-sm whitespace-pre-wrap leading-relaxed">{lead.project_need || "—"}</p>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info label="Fuente" value={lead.lead_source || "—"} />
            <Info label="Landing" value={lead.landing_page || "—"} />
            <Info label="UTM source" value={lead.utm_source || "—"} />
            <Info label="UTM campaign" value={lead.utm_campaign || "—"} />
            <Info label="Dispositivo" value={lead.device_type || "—"} />
            <Info label="Score" value={String(lead.lead_score ?? 0)} />
          </div>

          {/* Edit panel */}
          <div className="grad-border p-5 space-y-4">
            <h3 className="font-display font-bold text-white">Pipeline & seguimiento</h3>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <SmallLabel>Estado</SmallLabel>
                <select
                  name="lead_status"
                  value={form.lead_status}
                  onChange={onChange}
                  data-testid="drawer-lead-status"
                  className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-3 py-2.5 text-white text-sm focus:border-[#ff8a00] focus:ring-2 focus:ring-[#ff8a00]/20 transition outline-none"
                >
                  {STATUSES.map((s) => <option key={s} value={s} className="bg-[#0a0a0a]">{s}</option>)}
                </select>
              </div>
              <div>
                <SmallLabel>Prioridad</SmallLabel>
                <select
                  name="priority"
                  value={form.priority}
                  onChange={onChange}
                  data-testid="drawer-priority"
                  className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-3 py-2.5 text-white text-sm focus:border-[#ff8a00] focus:ring-2 focus:ring-[#ff8a00]/20 transition outline-none"
                >
                  {PRIORITIES.map((s) => <option key={s} value={s} className="bg-[#0a0a0a]">{s}</option>)}
                </select>
              </div>
              <div>
                <SmallLabel>Asignado a</SmallLabel>
                <input
                  name="assigned_to"
                  value={form.assigned_to}
                  onChange={onChange}
                  data-testid="drawer-assigned"
                  className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-3 py-2.5 text-white text-sm focus:border-[#ff8a00] focus:ring-2 focus:ring-[#ff8a00]/20 transition outline-none"
                />
              </div>
              <div>
                <SmallLabel>Score (0-100)</SmallLabel>
                <input
                  type="number"
                  min="0"
                  max="100"
                  name="lead_score"
                  value={form.lead_score}
                  onChange={onChange}
                  data-testid="drawer-score"
                  className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-3 py-2.5 text-white text-sm focus:border-[#ff8a00] focus:ring-2 focus:ring-[#ff8a00]/20 transition outline-none"
                />
              </div>
            </div>

            <div>
              <SmallLabel>Notas internas</SmallLabel>
              <textarea
                name="internal_notes"
                value={form.internal_notes}
                onChange={onChange}
                data-testid="drawer-notes"
                rows={4}
                placeholder="Próximo paso, contexto, propuesta enviada el día X..."
                className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:border-[#ff8a00] focus:ring-2 focus:ring-[#ff8a00]/20 transition outline-none resize-none"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <button onClick={save} disabled={saving} className="btn-primary !py-2.5 !px-5 text-sm disabled:opacity-60" data-testid="drawer-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? "Guardando..." : "Guardar cambios"}
              </button>
              <a
                href={waLink(`Hola ${lead.full_name?.split(" ")[0] || ""}, soy David Osorio. Recibí tu solicitud sobre "${lead.service_interest}". ¿Te queda bien hablar 15 min hoy o mañana?`)}
                target="_blank"
                rel="noreferrer"
                className="btn-secondary !py-2.5 !px-5 text-sm"
                data-testid="drawer-whatsapp"
              >
                <MessageSquare className="w-4 h-4" />
                Abrir WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value, link, target }) {
  const inner = (
    <>
      {Icon && <Icon className="w-3.5 h-3.5 text-[#ff8a00] mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 font-display">{label}</div>
        <div className="text-zinc-200 truncate">{value}</div>
      </div>
    </>
  );
  return link ? (
    <a href={link} target={target ? "_blank" : undefined} rel="noreferrer" className="flex items-start gap-2 glass rounded-lg px-3 py-2 hover:bg-white/[0.05] transition">{inner}</a>
  ) : (
    <div className="flex items-start gap-2 glass rounded-lg px-3 py-2">{inner}</div>
  );
}

function SmallLabel({ children }) {
  return <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 font-display font-semibold mb-1.5">{children}</div>;
}
